
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import PageTransition from "@/components/PageTransition";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Login from "@/components/dashboard/Login";
import LeadsList from "@/components/dashboard/LeadsList";
import { checkDashboardSession } from "@/utils/dashboardAuth";
import { dashboardApi } from "@/utils/dashboardAuth";
import { useToast } from "@/components/ui/use-toast";

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Check if user has a valid session
    const hasSession = checkDashboardSession();
    setIsAuthenticated(hasSession);
    setCheckingAuth(false);

    // If authenticated, verify the connection to the leads table
    if (hasSession) {
      const testConnection = async () => {
        try {
          let error: any = null;
          let count = 0;
          try {
            const res = await dashboardApi("list");
            count = res?.leads?.length ?? 0;
          } catch (e) {
            error = e;
          }

          if (error) {
            console.error("Failed to connect to database:", error);
            toast({
              variant: "destructive",
              title: "Database Connection Error",
              description: "There was an issue connecting to the database. Please try again later.",
            });
          } else {
            console.log("Successfully connected, found leads:", count);
          }
        } catch (err) {
          console.error("Unexpected error testing connection:", err);
        }
      };

      testConnection();
    }
  }, [toast]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    toast({
      title: "Login Successful",
      description: "You are now logged into the dashboard.",
    });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  return (
    <PageTransition>
      <Helmet>
        <title>Leads Dashboard | Los Angeles SEO Group</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Navbar />

      <section className="pt-24 pb-16 bg-gradient-to-r from-desert-canyon/10 to-desert-sunset/10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Leads <span className="text-gradient bg-canyon-gradient">Dashboard</span>
            </h1>
            <p className="text-xl text-foreground/70 mb-8 leading-relaxed">
              View and manage leads from contact forms, newsletter signups, and SEO tool usage.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-background">
        <div className="page-container">
          {checkingAuth ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-desert-sunset"></div>
            </div>
          ) : isAuthenticated ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <LeadsList onLogout={handleLogout} />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex justify-center py-10"
            >
              <Login onLoginSuccess={handleLoginSuccess} />
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
};

export default Dashboard;
